import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, from, Observable, throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { environment } from 'src/environments/environment';
import { EncryptionService } from '../loginServices/encryption.service';
import { catchError, switchMap } from 'rxjs/operators';
import { ConfigService } from '../loginServices/configservice.service';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {

  private userBranchDataBehaviour = new BehaviorSubject<any>(null);
	userData = this.userBranchDataBehaviour.asObservable();

  constructor(private http: HttpClient, private encryptionService: EncryptionService, private configService: ConfigService) { }

  private getKrakendUrl(): string {
    const krakendUrl = this.configService.getKrakendUrl();
    if (!krakendUrl) {
      throw new Error("Krakend URL is not available.");
    }
    return krakendUrl;
  }

  updateUserBranchData(branch: any) {
		this.userBranchDataBehaviour.next(branch);
	}

  getUserBranch() {
    const url = this.getKrakendUrl();
    if (url) {
      return this.http.get(url + '/user/branch');
    } else {
      return this.http.get(environment.apiUrl + '/user/branch');
    }
  }

  getCSOByBranch(branchId: any) {
    let params = {
      branch_id: branchId
    }
    const url = this.getKrakendUrl();
    if (url) {
      return this.http.get(url + '/user/cso/' + branchId);
    } else {
      return this.http.get(environment.apiUrl + '/user/cso/' + branchId);
    }
  }

  getConfirmQCData(search: string, branch_id: any) {
    const url = this.getKrakendUrl();
    if (url) {
      // Encrypt individual values: household_number and branch_id
      return forkJoin({
        encryptedSearch: from(this.encryptionService.encryptParameters({ value: search })),
        encryptedBranchId: from(this.encryptionService.encryptParameters({ value: branch_id.toString() }))
      }).pipe(
        switchMap((encryptedParams) => {
          // Prepare params with encrypted values
          const params = {
            household_number: encryptedParams.encryptedSearch.value, // Only encrypted value
            branch_id: encryptedParams.encryptedBranchId.value // Only encrypted value
          };

          // Send the encrypted params as part of the API request
          return this.http.get(url + "/household-cc-data", {
            params: params
          });
        }),
        catchError((error) => {
          console.error('Encryption failed:', error);
          // Handle the error or rethrow it
          return throwError(error);
        })
      );
    } else {
      // Perform the API request without encryption
      const params = {
        household_number: search,
        branch_id: branch_id
      };
      return this.http.get(environment.apiUrl + "/household-cc-data", {
        params: params
      });
    }
  }

  confirmQC(data: any): Observable<any> {
    const url = this.getKrakendUrl();
    if (url) {
      // Encrypt the data object
      return from(this.encryptionService.encryptParameters({ value: JSON.stringify(data) }))
        .pipe(
          switchMap((encryptedData) => {
            // Send the encrypted data in the POST request
            return this.http.post(url + '/confirm-qc', { encrypted_data: encryptedData.value });
          }),
          catchError((error) => {
            console.error('Encryption failed:', error);
            // Handle the error as needed
            throw error;
          })
        );
    } else {
      // Perform the API request without encryption
      return this.http.post(environment.apiUrl + '/confirm-qc', data);
    } 
  }

  getBankingDetail(household_id: any): Observable<any> {
    const url = this.getKrakendUrl();
    if (url) {
      // Encrypt only the household_id
      return from(this.encryptionService.encryptParameters({ value: household_id.toString() }))
        .pipe(
          switchMap((encryptedHouseholdId) => {
            // Prepare the params with the encrypted household_id
            let params = {
              household_id: encryptedHouseholdId.value // Use the encrypted household_id
            };

            // Send the GET request with the encrypted household_id in the params
            return this.http.get(url + '/banking-detail', { params: params });
          }),
          catchError((error) => {
            console.error('Encryption failed:', error);
            // Handle any encryption or request errors
            return throwError(error);
          })
        );
    } else {
      // Perform the API request without encryption
      let params = {
        household_id: household_id
      };
      return this.http.get(environment.apiUrl + '/banking-detail', { params: params });
    }
  }

  saveBankDetail(data: any): Observable<any> {
    const url = this.getKrakendUrl();
    if (url) {
      return from(this.encryptionService.encryptParameters({ value: JSON.stringify(data) }))
        .pipe(
          switchMap((encryptedData) => {
            // Prepare the API request with the encrypted data
            return this.http.post(url + "/save-bank-detail", { encrypted_data: encryptedData.value });
          }),
          catchError((error) => {
            console.error('Encryption failed:', error);
            // Handle the error properly
            return throwError(error);
          })
        );
    } else {
      // Perform the API request without encryption
      return this.http.post(environment.apiUrl + "/save-bank-detail", data);
    }
  }

  signatureVerification(hid: any) {
    let data = {
      household_id: hid
    }
    this.http.post(environment.php_url+'/forms/bank_signature_form.php', data,
    {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
      responseType: 'blob' 
    }).subscribe((response) => {
      let filename = 'Signature_Verification';
      this.downloadPdf(response, filename)
    });
  }
  
  downloadPdf(data: any, filename: any) {
    const blob = new Blob([data], { type: 'application/pdf' });
    const url= window.URL.createObjectURL(blob);
    
    // create <a> tag dinamically
    var fileLink = document.createElement('a');
    fileLink.href = url;

    // it forces the name of the downloaded file
    fileLink.download = filename;

    // triggers the click event
    fileLink.click();
    // window.open(url);
  }
}

